/**
 * Parallax product hero: scroll-driven translate + rotate on [data-parallax-layer] children.
 * Respects prefers-reduced-motion when data-respect-reduced is not "false".
 */
if (!customElements.get('parallax-product-hero')) {
  class ParallaxProductHero extends HTMLElement {
    constructor() {
      super();
      this._onScroll = this._onScroll.bind(this);
      this._onResize = this._onResize.bind(this);
      this._raf = null;
      this._io = null;
    }

    connectedCallback() {
      this.layers = [...this.querySelectorAll('[data-parallax-layer]')];
      if (!this.layers.length) return;

      this.motion = parseFloat(this.dataset.motionStrength || '0.65');
      this.mobileScale = parseFloat(this.dataset.mobileMotionScale || '0.45');
      this.respectReduced = this.dataset.respectReduced !== 'false';

      this._mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
      this._mqMobile = window.matchMedia('(max-width: 749px)');

      this._reducedMotion = this._mqReduce.matches;
      this._isMobile = this._mqMobile.matches;

      if (this._mqReduce.addEventListener) {
        this._mqReduce.addEventListener('change', this._onResize);
        this._mqMobile.addEventListener('change', this._onResize);
      } else {
        this._mqReduce.addListener(this._onResize);
        this._mqMobile.addListener(this._onResize);
      }

      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onResize);

      if ('IntersectionObserver' in window) {
        this._io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this._inView = entry.isIntersecting;
              if (this._inView) this._apply();
            });
          },
          { root: null, threshold: 0, rootMargin: '80px' }
        );
        this._io.observe(this);
      } else {
        this._inView = true;
      }

      this._apply();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onResize);
      if (this._mqReduce && this._mqReduce.removeEventListener) {
        this._mqReduce.removeEventListener('change', this._onResize);
        this._mqMobile.removeEventListener('change', this._onResize);
      }
      if (this._io) this._io.disconnect();
    }

    _onResize() {
      this._reducedMotion = this._mqReduce.matches;
      this._isMobile = this._mqMobile.matches;
      this._apply();
    }

    get _effectiveMotion() {
      if (this.respectReduced && this._reducedMotion) return 0;
      let m = Number.isFinite(this.motion) ? this.motion : 0.65;
      if (this._isMobile) m *= Number.isFinite(this.mobileScale) ? this.mobileScale : 0.45;
      return Math.max(0, Math.min(1.6, m));
    }

    _onScroll() {
      if (this._raf != null) return;
      this._raf = requestAnimationFrame(() => {
        this._apply();
        this._raf = null;
      });
    }

    _apply() {
      if (this._inView === false) return;
      const eff = this._effectiveMotion;
      const rect = this.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const vw = window.innerWidth || 1;
      const center = rect.top + rect.height / 2;
      const viewportCenter = vh / 2;
      /** Scroll progress: positive when section center is above viewport center (user scrolled down past) */
      const norm = (viewportCenter - center) / (vh * 0.48);
      const clamped = Math.max(-1.45, Math.min(1.45, norm));
      const sweepProgressRaw = (vh - rect.top) / (vh + Math.max(rect.height, 1));
      const sweepProgress = Math.max(0, Math.min(1, sweepProgressRaw));

      this.layers.forEach((el) => {
        if (eff === 0) {
          el.style.transform = 'translate3d(0, 0, 0) rotate(0deg)';
          return;
        }
        const axis = (el.dataset.axis || 'y').toLowerCase();
        const scrollMode = (el.dataset.scrollMode || '').toLowerCase();
        const depth = parseFloat(el.dataset.depth || '0.5');
        const maxRot = parseFloat(el.dataset.maxRotate || '0');

        if (axis === 'x' && scrollMode === 'sweep') {
          const speed = Math.max(0.05, Math.min(1.2, depth || 0.5));
          const travelFactor = Math.max(0.2, Math.min(1.6, parseFloat(el.dataset.travel || '1')));
          // Enter a bit earlier from the right, then let speed control
          // how much of the full right->left distance gets covered.
          const leadIn = 0.22;
          const enteredProgress = Math.max(0, Math.min(1, sweepProgress + leadIn));
          const sweepSpan = 0.15 + (speed * 1.85);
          const easedProgress = Math.max(0, Math.min(1, enteredProgress * sweepSpan));
          const travel = ((vw + el.offsetWidth) / 2) * travelFactor;
          const tx = (1 - 2 * easedProgress) * travel;
          el.style.transform = `translate3d(${tx.toFixed(2)}px, 0, 0)`;
          return;
        }

        /** Base px translate at full scroll progress; tuned for visible parallax */
        const amount = clamped * 88 * depth * eff;
        const tx = axis === 'x' ? amount : 0;
        const ty = axis === 'x' ? 0 : amount;
        const rot = axis === 'x' ? 0 : clamped * maxRot * eff;
        el.style.transform = `translate3d(${tx.toFixed(2)}px, ${ty.toFixed(2)}px, 0) rotate(${rot.toFixed(2)}deg)`;
      });
    }
  }

  customElements.define('parallax-product-hero', ParallaxProductHero);
}
